import React, { useEffect, useState } from "react";
import { BoardApi } from "../../../services/board-service-api";
import { LinkPreview } from "./types";

interface LinkPreviewCardProps {
  url: string;
  onDismiss: () => void;
}

// A discardable unfurl card for a URL found in a comment. The server returns sanitized og:* +
// <title> metadata (or nothing); we render nothing until it resolves, and nothing if there's no
// preview, so empty/blocked URLs leave no trace. The thumbnail is a plain <img> (can't execute
// script) that hides itself on load error.
const LinkPreviewCard: React.FC<LinkPreviewCardProps> = ({ url, onDismiss }) => {
  const [preview, setPreview] = useState<LinkPreview | null>(null);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [imageOk, setImageOk] = useState<boolean>(true);

  useEffect(() => {
    let cancelled: boolean = false;
    void BoardApi.getLinkPreview(url).then((next: LinkPreview | null) => {
      if (cancelled) return;
      setPreview(next);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!loaded || !preview) return null;

  return (
    <div className="kuika-board__link-preview">
      {preview.image && imageOk && (
        <img
          className="kuika-board__link-preview-thumb"
          src={preview.image}
          alt=""
          loading="lazy"
          onError={() => setImageOk(false)}
        />
      )}
      <a
        className="kuika-board__link-preview-body"
        href={preview.url}
        target="_blank"
        rel="noopener noreferrer nofollow"
      >
        {preview.siteName && <span className="kuika-board__link-preview-site">{preview.siteName}</span>}
        {preview.title && <span className="kuika-board__link-preview-title">{preview.title}</span>}
        {preview.description && <span className="kuika-board__link-preview-desc">{preview.description}</span>}
      </a>
      <button
        type="button"
        className="kuika-board__link-preview-dismiss"
        onClick={onDismiss}
        aria-label="Dismiss preview"
        title="Dismiss preview"
      >
        ×
      </button>
    </div>
  );
};

export default LinkPreviewCard;
